import React, { useState } from 'react';
import { useAppState } from '../../context/AppStateProvider';
import { FileText, Plus, Trash2, Edit2, X, Check, Copy } from 'lucide-react';

const WorkspacePrompts = () => {
  const { prompts, promptFolders, addPrompt, updatePrompt, deletePrompt, addPromptFolder, deletePromptFolder } = useAppState();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', text: '', folderId: '' });
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterFolder, setFilterFolder] = useState('');
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.name.trim() && formData.text.trim()) {
      if (editingId) {
        await updatePrompt(editingId, formData);
        setEditingId(null);
      } else {
        await addPrompt(formData.name.trim(), formData.text.trim(), formData.folderId || null);
      }
      setFormData({ name: '', text: '', folderId: '' });
      setShowForm(false);
    }
  };

  const handleEdit = (prompt) => {
    setEditingId(prompt.id);
    setFormData({ name: prompt.name, text: prompt.text, folderId: prompt.folderId || '' });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', text: '', folderId: '' });
  };

  const handleAddFolder = async (e) => {
    e.preventDefault();
    if (newFolderName.trim()) {
      await addPromptFolder(newFolderName.trim());
      setNewFolderName('');
      setShowFolderForm(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filter prompts
  const filteredPrompts = prompts.filter(prompt => {
    const matchesSearch = searchQuery === '' || 
      prompt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.text.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFolder = filterFolder === '' || 
      (filterFolder === 'uncategorized' ? !prompt.folderId : prompt.folderId === filterFolder);
    
    return matchesSearch && matchesFolder;
  });

  return (
    <div className="workspace-container" data-testid="workspace-prompts">
      <div className="workspace-header">
        <div className="workspace-header-left">
          <FileText className="workspace-icon" size={24} />
          <div>
            <h2 className="workspace-title">Prompts</h2>
            <p className="workspace-subtitle">{prompts.length} prompts saved</p>
          </div>
        </div>
        {!showForm && (
          <button 
            className="add-folder-btn"
            data-testid="add-prompt-btn"
            onClick={() => setShowForm(true)}
          >
            <Plus size={18} />
            New Prompt
          </button>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="workspace-filters">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search prompts..."
          className="search-input"
          data-testid="prompts-search"
        />
        <select
          value={filterFolder}
          onChange={(e) => setFilterFolder(e.target.value)}
          className="filter-select"
          data-testid="prompts-folder-filter"
        >
          <option value="">All Prompts</option>
          <option value="uncategorized">Uncategorized</option>
          {promptFolders.map(folder => (
            <option key={folder.id} value={folder.id}>{folder.name}</option>
          ))}
        </select>
        <button
          className="folder-manage-btn"
          data-testid="manage-folders-btn"
          onClick={() => setShowFolderForm(!showFolderForm)}
        >
          <Plus size={16} />
          {showFolderForm ? 'Cancel' : 'New Folder'}
        </button>
      </div>

      {/* Folder Creation Form */}
      {showFolderForm && (
        <div className="folder-form-container">
          <form onSubmit={handleAddFolder} className="folder-form">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name..."
              className="form-input"
              data-testid="folder-name-input"
              autoFocus
              required
            />
            <button type="submit" className="save-btn-small" data-testid="save-folder-btn">
              <Check size={16} />
            </button>
          </form>
          {promptFolders.length > 0 && (
            <div className="folder-list">
              <h4 className="folder-list-title">Existing Folders:</h4>
              {promptFolders.map(folder => (
                <div key={folder.id} className="folder-item">
                  <span>{folder.name}</span>
                  <button
                    onClick={() => deletePromptFolder(folder.id)}
                    className="folder-delete-btn"
                    data-testid={`delete-folder-${folder.id}`}
                    title="Delete folder"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="prompt-form-container" data-testid="prompt-form">
          <form onSubmit={handleSubmit} className="prompt-form">
            <div className="form-group">
              <label className="form-label">Prompt Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Client Onboarding Email"
                className="form-input"
                data-testid="prompt-name-input"
                autoFocus
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Folder (Optional)</label>
              <select
                value={formData.folderId}
                onChange={(e) => setFormData({ ...formData, folderId: e.target.value })}
                className="form-select"
                data-testid="prompt-folder-select"
              >
                <option value="">No folder</option>
                {promptFolders.map(folder => (
                  <option key={folder.id} value={folder.id}>{folder.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Prompt Text</label>
              <textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                placeholder="Enter your prompt text here..."
                className="prompt-textarea"
                data-testid="prompt-text-input"
                rows="8"
                required
              />
            </div>
            <div className="idea-form-actions">
              <button type="submit" className="save-btn" data-testid="save-prompt-btn">
                <Check size={16} />
                {editingId ? 'Update' : 'Save'}
              </button>
              <button type="button" onClick={handleCancel} className="cancel-btn" data-testid="cancel-prompt-btn">
                <X size={16} />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="prompts-list">
        {prompts.length === 0 && !showForm ? (
          <div className="empty-state-large" data-testid="empty-state">
            <FileText size={48} className="empty-icon" />
            <p>No prompts yet</p>
            <p className="empty-hint">Save your favorite prompts for quick access</p>
          </div>
        ) : filteredPrompts.length === 0 ? (
          <div className="empty-state-large">
            <FileText size={48} className="empty-icon" />
            <p>No prompts match your search</p>
            <p className="empty-hint">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="prompts-grid">
            {filteredPrompts.map(prompt => {
              const folder = promptFolders.find(f => f.id === prompt.folderId);
              return (
                <div key={prompt.id} className="prompt-card" data-testid={`prompt-${prompt.id}`}>
                  <div className="prompt-card-header">
                    <div>
                      <h3 className="prompt-card-title">{prompt.name}</h3>
                      {folder && (
                        <span className="prompt-folder-badge" data-testid={`prompt-folder-${prompt.id}`}>
                          {folder.name}
                        </span>
                      )}
                    </div>
                    <div className="idea-card-actions">
                      <button
                        className="idea-action-btn"
                        data-testid={`copy-prompt-${prompt.id}`}
                        onClick={() => copyToClipboard(prompt.text, prompt.id)}
                        title="Copy"
                      >
                        {copiedId === prompt.id ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                      <button
                        className="idea-action-btn"
                        data-testid={`edit-prompt-${prompt.id}`}
                        onClick={() => handleEdit(prompt)}
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="idea-action-btn idea-action-delete"
                        data-testid={`delete-prompt-${prompt.id}`}
                        onClick={() => deletePrompt(prompt.id)}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="prompt-card-text">{prompt.text}</p>
                  <div className="prompt-card-footer">
                    <span className="idea-timestamp">
                      {new Date(prompt.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspacePrompts;
